export const IMAGE_MIGRATION_COMPATIBILITY_VERSION = "portal-image-migration-v1";

const ENVELOPE_PATHS = Object.freeze([
  "metadata.imageOptimization",
  "imageOptimization",
  "heroMedia.metadata.imageOptimization",
  "heroMedia.slots.hero.metadata.imageOptimization",
  "heroMedia.slots.thumbnail.metadata.imageOptimization",
  "thumbnailMedia.metadata.imageOptimization",
  "seo.ogImage.metadata.imageOptimization",
  "media.metadata.imageOptimization",
  "mediaSuite.metadata.imageOptimization",
  "projection.imageOptimization",
  "compatibility.imageOptimization",
]);

const DELIVERY_PATHS = Object.freeze([
  "metadata.delivery",
  "binding.delivery",
  "asset.binding.delivery",
  "imageBinding.delivery",
  "optimization.delivery",
  "projection.delivery",
  "compatibility.delivery",
]);

const READY_STATUSES = new Set(["", "ready", "completed", "complete", "succeeded", "available", "published", "active"]);

const text = (value = "") => String(value ?? "").trim();

const objectValue = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const getPathValue = (input, path = "") =>
  text(path)
    .split(".")
    .filter(Boolean)
    .reduce((current, segment) => {
      if (current === undefined || current === null) return undefined;
      if (current instanceof Map) return current.get(segment);
      return current[segment];
    }, input);

const formatFromUrl = (value = "") => {
  const match = text(value).split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i);
  const format = text(match?.[1]).toLowerCase();
  return format === "jpeg" ? "jpg" : format;
};

const variantKeyFromUrl = (value = "") => {
  const clean = text(value).split(/[?#]/)[0];
  const name = clean.slice(clean.lastIndexOf("/") + 1);
  return name.replace(/\.[a-z0-9]+$/i, "");
};

export const inferImageDeliveryBaseUrl = (value = "") => {
  const raw = text(value);
  if (!/^https?:\/\//i.test(raw)) return "";
  const mediaIndex = raw.toLowerCase().indexOf("/media/");
  if (mediaIndex > 0) return raw.slice(0, mediaIndex).replace(/\/+$/, "");
  try {
    return new URL(raw).origin;
  } catch (_error) {
    return "";
  }
};

const normalizeVersion = (value) => {
  const token = text(value || 1).replace(/^v/i, "") || "1";
  const numeric = Number(token);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : token;
};

const normalizeVariant = (value, defaults = {}) => {
  const entry = typeof value === "string" ? { url: value } : objectValue(value);
  if (!entry) return null;
  const status = text(entry.status).toLowerCase();
  if (!READY_STATUSES.has(status)) return null;
  const url = text(
    entry.url ||
    entry.path ||
    entry.storageUrl ||
    entry.publicUrl ||
    entry.variantUrl,
  );
  if (!url) return null;
  const slotKey = text(entry.slotKey || entry.outputSlot || entry.slot || defaults.slotKey);
  if (!slotKey) return null;
  const role = text(entry.role || entry.deviceRole || entry.sourceRole || defaults.role || "default").toLowerCase();
  const variantKey = text(
    entry.variantKey ||
    entry.variant ||
    entry.sizeKey ||
    entry.outputKey ||
    defaults.variantKey ||
    variantKeyFromUrl(url),
  );
  const format = text(entry.format || defaults.format || formatFromUrl(url) || "webp").toLowerCase();
  return {
    slotKey,
    role: role || "default",
    variantKey,
    format: format === "jpeg" ? "jpg" : format,
    path: url,
    width: Number(entry.width || defaults.width || 0) || null,
    height: Number(entry.height || defaults.height || 0) || null,
  };
};

const normalizeVariantMap = (source = {}) => {
  const result = [];
  Object.entries(objectValue(source) || {}).forEach(([slotKey, slotValue]) => {
    if (Array.isArray(slotValue)) {
      slotValue.forEach((entry) => {
        const normalized = normalizeVariant(entry, { slotKey });
        if (normalized) result.push(normalized);
      });
      return;
    }
    if (typeof slotValue === "string" || objectValue(slotValue)?.url || objectValue(slotValue)?.path) {
      const normalized = normalizeVariant(slotValue, { slotKey });
      if (normalized) result.push(normalized);
      return;
    }
    Object.entries(objectValue(slotValue) || {}).forEach(([role, roleValue]) => {
      const normalized = normalizeVariant(roleValue, { slotKey, role });
      if (normalized) result.push(normalized);
    });
  });
  return result;
};

const normalizeVariants = (source) => {
  if (Array.isArray(source)) return source.map((entry) => normalizeVariant(entry)).filter(Boolean);
  return normalizeVariantMap(source);
};

const findEnvelope = (value) => {
  if (!value) return { envelope: null, sourcePath: "" };
  if (Array.isArray(value)) {
    for (const item of value) {
      const match = findEnvelope(item);
      if (match.envelope) return match;
    }
    return { envelope: null, sourcePath: "" };
  }
  const input = objectValue(value);
  if (!input) return { envelope: null, sourcePath: "" };
  if (objectValue(input.delivery)) return { envelope: input, sourcePath: "delivery" };
  for (const path of ENVELOPE_PATHS) {
    const candidate = objectValue(getPathValue(input, path));
    if (candidate) return { envelope: candidate, sourcePath: path };
  }
  for (const path of DELIVERY_PATHS) {
    const candidate = objectValue(getPathValue(input, path));
    if (candidate) return { envelope: { delivery: candidate }, sourcePath: path };
  }
  return { envelope: null, sourcePath: "" };
};

const normalizeIdentity = (envelope = {}, delivery = null) => {
  const raw = delivery || {};
  const fallbackUrl = text(
    raw.fallbackUrl ||
    raw.recommendedUrl ||
    raw.url ||
    envelope.fallbackUrl ||
    envelope.recommendedUrl ||
    envelope.url,
  );
  return {
    imageCode: text(raw.imageCode || raw.imageId || envelope.imageCode || envelope.imageId),
    version: normalizeVersion(raw.version || raw.assetVersion || envelope.version || envelope.assetVersion),
    presetKey: text(raw.presetKey || raw.preset || envelope.presetKey || envelope.preset).toLowerCase(),
    baseUrl: text(raw.baseUrl || raw.cdnBase || raw.storageBaseUrl || envelope.baseUrl || envelope.cdnBase) || inferImageDeliveryBaseUrl(fallbackUrl),
    format: text(raw.format || envelope.format || "webp").toLowerCase(),
  };
};

export const normalizeImageMigrationPayload = (value) => {
  const { envelope, sourcePath } = findEnvelope(value);
  if (!envelope) {
    return {
      contractVersion: IMAGE_MIGRATION_COMPATIBILITY_VERSION,
      metadata: null,
      delivery: null,
      identity: null,
      sourcePath: "",
      state: "legacy",
    };
  }

  const directDelivery = objectValue(envelope.delivery);
  const legacyVariants = envelope.variants || envelope.outputs || envelope.generatedVariants;
  const hasLegacyDescriptor = !directDelivery && Boolean(legacyVariants);
  const rawDelivery = directDelivery || (hasLegacyDescriptor ? envelope : null);
  const identity = normalizeIdentity(envelope, rawDelivery);
  let delivery = null;

  if (rawDelivery) {
    const variants = normalizeVariants(
      rawDelivery.variants || rawDelivery.outputs || rawDelivery.generatedVariants,
    );
    const fallbackUrl = text(
      rawDelivery.fallbackUrl ||
      rawDelivery.recommendedUrl ||
      rawDelivery.url ||
      envelope.fallbackUrl ||
      envelope.recommendedUrl ||
      envelope.url,
    );
    const firstVariantUrl = variants[0]?.path || "";
    delivery = {
      contractVersion: Number(rawDelivery.contractVersion || envelope.deliveryContractVersion || 1) || 1,
      imageCode: identity.imageCode,
      version: identity.version,
      presetKey: identity.presetKey,
      baseUrl: identity.baseUrl || inferImageDeliveryBaseUrl(fallbackUrl || firstVariantUrl),
      fallbackUrl,
      blurUrl: text(rawDelivery.blurUrl || rawDelivery.placeholderUrl || envelope.blurUrl || envelope.placeholderUrl),
      thumbnailUrl: text(rawDelivery.thumbnailUrl || rawDelivery.thumbUrl || envelope.thumbnailUrl || envelope.thumbUrl),
      variants,
    };
  }

  return {
    contractVersion: IMAGE_MIGRATION_COMPATIBILITY_VERSION,
    metadata: envelope,
    delivery,
    identity: identity.imageCode ? identity : null,
    sourcePath,
    state: delivery ? (delivery.variants.length ? "projected" : "projection-fallback") : identity.imageCode ? "partial" : "legacy",
  };
};

export const extractCompatibleImageOptimizationMetadata = (value) =>
  normalizeImageMigrationPayload(value).metadata;

export const extractCompatibleImageDelivery = (value) =>
  normalizeImageMigrationPayload(value).delivery;

export default normalizeImageMigrationPayload;
